import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

const FIELDS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name',  label: 'Last name'  },
  { key: 'email',      label: 'Email'      },
  { key: 'phone',      label: 'Phone'      },
  { key: 'company',    label: 'Company'    },
  { key: 'job_title',  label: 'Job title'  },
];

/**
 * Modal for inline-editing a single contact. Missing variables (from a
 * campaign step) are highlighted so the user knows what to fill in to
 * unblock the send. Saving PATCHes /contacts/{id} and — if all missing
 * fields are now filled — the backend automatically re-queues the contact.
 */
export default function EditContactDialog({ open, onOpenChange, contact, missingFields = [], onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (contact) {
      setForm({
        first_name: contact.first_name || '',
        last_name:  contact.last_name  || '',
        email:      contact.email      || '',
        phone:      contact.phone      || '',
        company:    contact.company    || '',
        job_title:  contact.job_title  || '',
      });
    }
  }, [contact]);

  if (!contact) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/contacts/${contact.id}`, form);
      toast.success('Contact updated');
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const missingSet = new Set(missingFields || []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111110] border-zinc-800 text-zinc-100 max-w-lg" data-testid="edit-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit contact</DialogTitle>
        </DialogHeader>
        {missingSet.size > 0 && (
          <div className="text-xs text-orange-300 border border-orange-900/70 bg-orange-950/40 rounded-md px-3 py-2">
            Fill in the highlighted fields to re-queue this contact for sending.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 mt-1">
          {FIELDS.map(f => {
            const hi = missingSet.has(f.key);
            return (
              <div key={f.key} className={f.key==='email' || f.key==='phone' ? 'col-span-2' : ''}>
                <Label htmlFor={`ec-${f.key}`} className={`text-xs ${hi ? 'text-orange-300' : 'text-zinc-500'}`}>
                  {f.label}{hi && <span className="ml-1">·  missing</span>}
                </Label>
                <Input
                  id={`ec-${f.key}`}
                  data-testid={`edit-contact-${f.key.replace('_','-')}`}
                  value={form[f.key] || ''}
                  onChange={e => set(f.key, e.target.value)}
                  className={`mt-1 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] ${hi ? 'border-orange-800' : ''}`}
                />
              </div>
            );
          })}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={() => onOpenChange(false)} data-testid="edit-contact-cancel">Cancel</Button>
          <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={save} disabled={saving} data-testid="edit-contact-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
